import React from 'react';
import {
	AbsoluteFill,
	spring,
	useCurrentFrame,
	useVideoConfig,
	interpolate,
	Easing,
} from 'remotion';

/**
 * 一滴水的旅程：从山泉到大海
 * 分四个阶段叙事：山泉涌出 → 溪流奔腾 → 河谷孕育生命 → 汇入大海
 */
export const RiverFlowSimulation: React.FC = () => {
	const frame = useCurrentFrame();
	const {fps, durationInFrames} = useVideoConfig();

	const totalProgress = frame / durationInFrames;

	const phaseProgress = (phaseStart: number, phaseDuration: number) => {
		const p = (totalProgress - phaseStart) / phaseDuration;
		return Math.max(0, Math.min(1, p));
	};

	// 四阶段时间线（各占 25%）
	const phase1 = phaseProgress(0, 0.25); // 山泉涌出
	const phase2 = phaseProgress(0.25, 0.25); // 溪流奔腾
	const phase3 = phaseProgress(0.5, 0.25); // 河谷生命
	const phase4 = phaseProgress(0.75, 0.25); // 汇入大海

	// 背景色随旅程变化：山间清晨 → 午后河谷 → 黄昏入海
	const bgTop = interpolate(totalProgress, [0, 0.5, 1], [220, 210, 25], {
		extrapolateRight: 'clamp',
	});
	const bgSat = interpolate(totalProgress, [0, 0.5, 1], [60, 55, 70], {
		extrapolateRight: 'clamp',
	});
	const bgLight = interpolate(totalProgress, [0, 0.5, 1], [85, 80, 65], {
		extrapolateRight: 'clamp',
	});

	// 主角水滴的位置随旅程推进
	const dropX = interpolate(totalProgress, [0, 0.2, 0.5, 0.8, 1], [200, 450, 960, 1400, 1750], {
		extrapolateRight: 'clamp',
		easing: Easing.inOut(Easing.ease),
	});
	const dropY = interpolate(totalProgress, [0, 0.15, 0.3, 0.6, 0.85, 1], [200, 300, 450, 520, 560, 600], {
		extrapolateRight: 'clamp',
	});
	const dropSize = interpolate(totalProgress, [0, 0.5, 1], [18, 24, 32], {
		extrapolateRight: 'clamp',
	});
	const dropBob = Math.sin(frame * 0.15) * 6;

	// 山脉（Phase 1 背景）
	const mountains = [
		{x: 0, peakY: 180, width: 500, color: '#5D7B6F'},
		{x: 200, peakY: 120, width: 600, color: '#4A6B5A'},
		{x: 500, peakY: 200, width: 450, color: '#6B8F7E'},
		{x: -100, peakY: 250, width: 400, color: '#7BA08D'},
	];

	// 山泉水滴（Phase 1 涌出效果）
	const springDrops = Array.from({length: 12}, (_, i) => {
		const delay = i * 4;
		const life = ((frame - delay) % 40) / 40;
		const active = frame > delay && phase1 > 0.1;
		return {
			id: i,
			x: 220 + Math.sin(i * 2.5) * 30 + life * 60,
			y: 280 + life * 100 - Math.sin(life * Math.PI) * 40,
			size: active ? 4 + (1 - life) * 6 : 0,
			opacity: active ? (1 - life) * 0.8 : 0,
		};
	});

	// 溪流粒子（Phase 2 水花）
	const streamParticles = Array.from({length: 40}, (_, i) => {
		const seed = i * 47;
		const speed = 2 + Math.abs(Math.sin(seed)) * 3;
		const flowX = (350 + (frame * speed + seed * 3) % 600);
		const flowY = 400 + Math.sin((frame + seed) * 0.08) * 25 + (i % 5) * 15;
		return {
			id: i,
			x: flowX,
			y: flowY,
			size: 3 + Math.abs(Math.sin(seed)) * 4,
			opacity: phase2 > 0 ? Math.min(phase2 * 3, 0.7) : 0,
		};
	});

	// 河谷中的鱼（Phase 3 生命出现）
	const fishes = Array.from({length: 8}, (_, i) => {
		const seed = i * 120;
		const appear = spring({
			frame: frame - durationInFrames * 0.52 - i * 8,
			fps,
			config: {damping: 15, stiffness: 80},
		});
		const swimX = 700 + Math.sin((frame * 0.02) + seed) * 120 + i * 80;
		const swimY = 480 + Math.sin((frame * 0.03) + seed) * 30;
		return {
			id: i,
			x: swimX,
			y: swimY,
			size: 14 + (i % 3) * 4,
			opacity: Math.max(0, appear),
			color: `hsl(${20 + i * 15}, 75%, 55%)`,
			flip: Math.sin(seed) > 0,
		};
	});

	// 河岸植物（Phase 3 生长）
	const plants = Array.from({length: 14}, (_, i) => {
		const growSpring = spring({
			frame: frame - durationInFrames * 0.5 - i * 6,
			fps,
			config: {damping: 12, stiffness: 60},
		});
		return {
			id: i,
			x: 600 + i * 70,
			y: i % 2 === 0 ? 440 : 570,
			height: (25 + (i % 4) * 10) * Math.max(0, growSpring),
			sway: Math.sin((frame + i * 30) * 0.06) * 8,
			color: `hsl(${110 + i * 5}, 55%, ${30 + (i % 3) * 5}%)`,
		};
	});

	// 海浪（Phase 4）
	const waveOffset = frame * 1.5;

	// 海鸥（Phase 4 点缀）
	const seagulls = Array.from({length: 5}, (_, i) => {
		const appear = spring({
			frame: frame - durationInFrames * 0.8 - i * 10,
			fps,
			config: {damping: 20, stiffness: 50},
		});
		return {
			id: i,
			x: 1300 + i * 100 + Math.sin((frame + i * 40) * 0.02) * 60,
			y: 120 + i * 40 + Math.sin((frame + i * 20) * 0.04) * 20,
			wingAngle: Math.sin((frame + i * 15) * 0.12) * 25,
			opacity: Math.max(0, appear),
			size: 16 + i * 2,
		};
	});

	// 阶段文字
	const stageTexts = [
		{label: '山泉涌出', sub: '一滴水从岩缝中诞生', phase: phase1, x: 220, y: 130},
		{label: '溪流奔腾', sub: '跃过石头，穿越山谷', phase: phase2, x: 550, y: 340},
		{label: '河谷孕育生命', sub: '鱼虾嬉戏，水草摇曳', phase: phase3, x: 900, y: 380},
		{label: '汇入大海', sub: '千川归海，生生不息', phase: phase4, x: 1500, y: 300},
	];

	return (
		<AbsoluteFill
			style={{
				background: `linear-gradient(180deg, hsl(${bgTop}, ${bgSat}%, ${bgLight}%) 0%, hsl(${bgTop + 10}, ${bgSat - 10}%, ${bgLight - 15}%) 100%)`,
				overflow: 'hidden',
			}}
		>
			{/* 标题 */}
			<div
				style={{
					position: 'absolute',
					top: 28,
					left: 0,
					right: 0,
					textAlign: 'center',
					zIndex: 100,
					opacity: interpolate(frame, [0, 30, durationInFrames - 30, durationInFrames], [0, 1, 1, 0], {extrapolateRight: 'clamp', extrapolateLeft: 'clamp'}),
				}}
			>
				<div style={{fontSize: 52, fontWeight: 800, color: '#1A5276', letterSpacing: 6, textShadow: '0 2px 12px rgba(255,255,255,0.6)'}}>
					一滴水的旅程
				</div>
				<div style={{fontSize: 30, color: '#2980B9', marginTop: 4, letterSpacing: 3}}>
					从山泉到大海
				</div>
			</div>

			{/* ===== Phase 1: 山脉 ===== */}
			<svg style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}} viewBox="0 0 1920 1080" preserveAspectRatio="none">
				{mountains.map((m, i) => {
					const mOpacity = interpolate(phase1, [0, 0.3], [0, 1], {extrapolateRight: 'clamp'});
					return (
						<polygon
							key={i}
							points={`${m.x},700 ${m.x + m.width / 2},${m.peakY} ${m.x + m.width},700`}
							fill={m.color}
							opacity={mOpacity}
						/>
					);
				})}
				{/* 雪顶 */}
				{phase1 > 0.2 && mountains.slice(0, 2).map((m, i) => (
					<polygon
						key={`snow-${i}`}
						points={`${m.x + m.width * 0.35},${m.peakY + 80} ${m.x + m.width / 2},${m.peakY} ${m.x + m.width * 0.65},${m.peakY + 80}`}
						fill="white"
						opacity={interpolate(phase1, [0.2, 0.5], [0, 0.85], {extrapolateRight: 'clamp'})}
					/>
				))}
			</svg>

			{/* 山泉水滴 */}
			{springDrops.map((d) => (
				<div
					key={`spring-${d.id}`}
					style={{
						position: 'absolute',
						left: d.x,
						top: d.y,
						width: d.size,
						height: d.size,
						borderRadius: '50%',
						background: 'radial-gradient(circle at 35% 35%, #E8F8FF, #64B5F6)',
						opacity: d.opacity,
					}}
				/>
			))}

			{/* ===== Phase 2: 溪流 ===== */}
			{phase2 > 0 && (
				<svg style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none'}} viewBox="0 0 1920 1080">
					<path
						d={`M 250 350 C 400 380, 500 420, 650 440 C 800 460, 900 455, 1000 460`}
						fill="none"
						stroke="#42A5F5"
						strokeWidth={interpolate(phase2, [0, 1], [2, 30], {extrapolateRight: 'clamp'})}
						opacity={Math.min(phase2 * 2, 0.6)}
						strokeLinecap="round"
					/>
					{/* 溪流石头 */}
					{[{cx: 420, cy: 400}, {cx: 560, cy: 430}, {cx: 700, cy: 445}].map((rock, i) => (
						<ellipse
							key={i}
							cx={rock.cx}
							cy={rock.cy}
							rx={12 + i * 3}
							ry={8 + i * 2}
							fill="#8D8D8D"
							opacity={interpolate(phase2, [0.2, 0.5], [0, 0.8], {extrapolateRight: 'clamp'})}
						/>
					))}
				</svg>
			)}

			{/* 溪流水花粒子 */}
			{streamParticles.map((p) => (
				<div
					key={`stream-${p.id}`}
					style={{
						position: 'absolute',
						left: p.x,
						top: p.y,
						width: p.size,
						height: p.size,
						borderRadius: '50%',
						background: '#90CAF9',
						opacity: p.opacity,
						filter: 'blur(0.5px)',
					}}
				/>
			))}

			{/* ===== Phase 3: 河谷宽河 ===== */}
			{phase3 > 0 && (
				<div
					style={{
						position: 'absolute',
						top: 450,
						left: 600,
						width: interpolate(phase3, [0, 0.6], [0, 800], {extrapolateRight: 'clamp'}),
						height: interpolate(phase3, [0, 0.6], [10, 140], {extrapolateRight: 'clamp'}),
						background: 'linear-gradient(180deg, rgba(66,165,245,0.7) 0%, rgba(30,136,229,0.8) 100%)',
						borderRadius: '8px',
						overflow: 'hidden',
					}}
				>
					{/* 河面波光 */}
					{Array.from({length: 8}, (_, i) => (
						<div
							key={i}
							style={{
								position: 'absolute',
								left: ((frame * 2 + i * 100) % 820) - 20,
								top: 10 + i * 16,
								width: 50 + i * 5,
								height: 2,
								background: 'rgba(255,255,255,0.4)',
								borderRadius: 1,
							}}
						/>
					))}
				</div>
			)}

			{/* 河岸植物 */}
			{plants.map((p) => (
				<div
					key={`plant-${p.id}`}
					style={{
						position: 'absolute',
						left: p.x,
						top: p.y - p.height,
						width: 4,
						height: p.height,
						background: p.color,
						borderRadius: '2px 2px 0 0',
						transform: `rotate(${p.sway}deg)`,
						transformOrigin: 'bottom center',
					}}
				>
					{p.height > 15 && (
						<div style={{
							position: 'absolute',
							top: 0,
							left: -4,
							width: 12,
							height: 8,
							borderRadius: '50%',
							background: p.color,
							filter: 'brightness(1.2)',
						}} />
					)}
				</div>
			))}

			{/* 鱼 */}
			{fishes.map((f) => (
				<svg
					key={`fish-${f.id}`}
					style={{
						position: 'absolute',
						left: f.x,
						top: f.y,
						opacity: f.opacity,
						transform: f.flip ? 'scaleX(-1)' : undefined,
					}}
					width={f.size * 2.5}
					height={f.size * 1.2}
					viewBox="0 0 40 20"
				>
					<ellipse cx="18" cy="10" rx="14" ry="7" fill={f.color} />
					<polygon points="32,10 40,4 40,16" fill={f.color} />
					<circle cx="10" cy="8" r="2" fill="white" />
					<circle cx="10" cy="8" r="1" fill="#333" />
				</svg>
			))}

			{/* ===== Phase 4: 大海 ===== */}
			{phase4 > 0 && (
				<>
					{/* 海面 */}
					<svg style={{position: 'absolute', bottom: 0, left: 0, width: '100%', height: 400, pointerEvents: 'none'}} viewBox="0 0 1920 400" preserveAspectRatio="none">
						<defs>
							<linearGradient id="seaGrad" x1="0" y1="0" x2="0" y2="1">
								<stop offset="0%" stopColor="#2196F3" stopOpacity={0.6 * Math.min(phase4 * 2, 1)} />
								<stop offset="100%" stopColor="#0D47A1" stopOpacity={0.9 * Math.min(phase4 * 2, 1)} />
							</linearGradient>
						</defs>
						<path
							d={`M 0 ${100 + Math.sin(waveOffset * 0.03) * 15}
								${Array.from({length: 20}, (_, i) => {
									const wx = i * 100;
									const wy = 100 + Math.sin((waveOffset * 0.03) + i * 0.6) * 20;
									return `Q ${wx + 50} ${wy - 25}, ${wx + 100} ${wy}`;
								}).join(' ')}
								L 1920 400 L 0 400 Z`}
							fill="url(#seaGrad)"
						/>
						{/* 第二层波浪 */}
						<path
							d={`M 0 ${140 + Math.sin(waveOffset * 0.025 + 1) * 12}
								${Array.from({length: 20}, (_, i) => {
									const wx = i * 100;
									const wy = 140 + Math.sin((waveOffset * 0.025 + 1) + i * 0.5) * 18;
									return `Q ${wx + 50} ${wy - 20}, ${wx + 100} ${wy}`;
								}).join(' ')}
								L 1920 400 L 0 400 Z`}
							fill="#1565C0"
							opacity={0.4 * Math.min(phase4 * 2, 1)}
						/>
					</svg>

					{/* 海面光斑 */}
					{Array.from({length: 12}, (_, i) => {
						const sparkleOpacity = Math.sin((frame + i * 20) * 0.1) * 0.3 + 0.3;
						return (
							<div
								key={`sparkle-${i}`}
								style={{
									position: 'absolute',
									left: 1200 + (i % 6) * 110 + Math.sin(i * 3) * 40,
									top: 580 + Math.floor(i / 6) * 60 + Math.sin((frame + i * 10) * 0.05) * 10,
									width: 4,
									height: 4,
									borderRadius: '50%',
									background: 'white',
									opacity: sparkleOpacity * Math.min(phase4 * 3, 1),
									boxShadow: '0 0 6px rgba(255,255,255,0.6)',
								}}
							/>
						);
					})}
				</>
			)}

			{/* 海鸥 */}
			{seagulls.map((g) => (
				<svg
					key={`gull-${g.id}`}
					style={{position: 'absolute', left: g.x, top: g.y, opacity: g.opacity}}
					width={g.size * 3}
					height={g.size * 1.5}
					viewBox="0 0 50 25"
				>
					<path
						d={`M 10 15 Q 18 ${15 - g.wingAngle * 0.4} 25 12 Q 32 ${15 - g.wingAngle * 0.4} 40 15`}
						fill="none"
						stroke="#444"
						strokeWidth="2.5"
						strokeLinecap="round"
					/>
				</svg>
			))}

			{/* ===== 主角水滴 ===== */}
			<div
				style={{
					position: 'absolute',
					left: dropX - dropSize / 2,
					top: dropY + dropBob - dropSize / 2,
					width: dropSize,
					height: dropSize * 1.3,
					zIndex: 50,
					filter: 'drop-shadow(0 2px 8px rgba(33,150,243,0.5))',
				}}
			>
				<svg width={dropSize} height={dropSize * 1.3} viewBox="0 0 24 32">
					<path
						d="M12 0 C12 0 2 14 2 22 C2 28 6.5 32 12 32 C17.5 32 22 28 22 22 C22 14 12 0 12 0Z"
						fill="url(#dropGrad)"
					/>
					<defs>
						<radialGradient id="dropGrad" cx="40%" cy="40%">
							<stop offset="0%" stopColor="#E3F2FD" />
							<stop offset="60%" stopColor="#42A5F5" />
							<stop offset="100%" stopColor="#1565C0" />
						</radialGradient>
					</defs>
					<ellipse cx="9" cy="16" rx="3" ry="5" fill="rgba(255,255,255,0.35)" transform="rotate(-20 9 16)" />
				</svg>
			</div>

			{/* 主角水滴发光效果 */}
			<div
				style={{
					position: 'absolute',
					left: dropX - dropSize * 1.5,
					top: dropY + dropBob - dropSize * 1.5,
					width: dropSize * 3,
					height: dropSize * 3,
					borderRadius: '50%',
					background: 'radial-gradient(circle, rgba(66,165,245,0.25) 0%, transparent 70%)',
					zIndex: 49,
					animation: 'none',
					opacity: 0.6 + Math.sin(frame * 0.1) * 0.3,
				}}
			/>

			{/* 阶段文字 */}
			{stageTexts.map((st, i) => {
				const textOpacity = interpolate(st.phase, [0.05, 0.25, 0.7, 1], [0, 1, 1, 0], {
					extrapolateRight: 'clamp',
					extrapolateLeft: 'clamp',
				});
				const textY = interpolate(st.phase, [0.05, 0.25], [20, 0], {extrapolateRight: 'clamp'});
				return (
					<div
						key={i}
						style={{
							position: 'absolute',
							left: st.x,
							top: st.y,
							transform: `translate(-50%, ${textY}px)`,
							textAlign: 'center',
							opacity: textOpacity,
							zIndex: 80,
						}}
					>
						<div style={{fontSize: 30, fontWeight: 700, color: '#1A5276', textShadow: '0 1px 8px rgba(255,255,255,0.7)', whiteSpace: 'nowrap'}}>
							{st.label}
						</div>
						<div style={{fontSize: 24, color: '#2C3E50', marginTop: 4, textShadow: '0 1px 4px rgba(255,255,255,0.5)', whiteSpace: 'nowrap'}}>
							{st.sub}
						</div>
					</div>
				);
			})}

			{/* 底部时间线指示 */}
			<div
				style={{
					position: 'absolute',
					bottom: 30,
					left: 100,
					right: 100,
					height: 4,
					background: 'rgba(255,255,255,0.3)',
					borderRadius: 2,
					zIndex: 90,
				}}
			>
				<div
					style={{
						width: `${totalProgress * 100}%`,
						height: '100%',
						background: 'linear-gradient(90deg, #81D4FA, #1E88E5, #0D47A1)',
						borderRadius: 2,
						transition: 'width 0.1s',
					}}
				/>
				{/* 四个节点 */}
				{['🏔️', '🏞️', '🐟', '🌊'].map((icon, i) => (
					<div
						key={i}
						style={{
							position: 'absolute',
							left: `${i * 33.3}%`,
							top: -12,
							transform: 'translateX(-50%)',
							fontSize: 18,
							opacity: totalProgress >= i * 0.25 ? 1 : 0.35,
						}}
					>
						{icon}
					</div>
				))}
			</div>
		</AbsoluteFill>
	);
};
